// src/components/music/hooks/usePlaylist.js

import { useState, useEffect, useCallback } from 'react';
import { db } from '../../../firebase';
import {
  ref as dbRef,
  set,
  get,
  update,
  remove,
  onValue,
  off
} from 'firebase/database';

export const usePlaylist = (currentUser) => {
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const userId = currentUser?.uid;

  // Fetch all playlists for the user
  useEffect(() => {
    if (!userId) {
      setPlaylists([]);
      return;
    }

    setLoading(true);
    const playlistsRef = dbRef(db, `users/${userId}/playlists`);

    onValue(
      playlistsRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const playlistsArray = Object.entries(data).map(([key, value]) => ({
            id: key,
            ...value,
            tracks: value.tracks || []
          }));
          setPlaylists(playlistsArray);
        } else {
          setPlaylists([]);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching playlists:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => off(playlistsRef);
  }, [userId]);

  // Create a new playlist
  const createPlaylist = useCallback(
    async (name, description = '') => {
      if (!userId) {
        setError('User not authenticated');
        return null;
      }

      try {
        const playlistId = `playlist_${Date.now()}`;
        const newPlaylist = {
          id: playlistId,
          name,
          description,
          tracks: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        const playlistRef = dbRef(db, `users/${userId}/playlists/${playlistId}`);
        await set(playlistRef, newPlaylist);
        setError(null);
        return newPlaylist;
      } catch (err) {
        const errorMsg = err.message;
        setError(errorMsg);
        console.error('Error creating playlist:', err);
        return null;
      }
    },
    [userId]
  );

  // Add track to playlist
  const addTrackToPlaylist = useCallback(
    async (playlistId, track) => {
      if (!userId) {
        setError('User not authenticated');
        return false;
      }

      try {
        const playlistRef = dbRef(db, `users/${userId}/playlists/${playlistId}`);
        const snapshot = await get(playlistRef);
        const playlist = snapshot.val();

        if (!playlist) {
          setError('Playlist not found');
          return false;
        }

        // Check if track already exists
        const tracks = playlist.tracks || [];
        const trackExists = tracks.some(t => t.id === track.id);

        if (trackExists) {
          setError('Track already in playlist');
          return false;
        }

        const updatedTracks = [
          ...tracks,
          {
            ...track,
            addedAt: new Date().toISOString()
          }
        ];

        await update(playlistRef, {
          tracks: updatedTracks,
          updatedAt: new Date().toISOString()
        });

        // Update selectedPlaylist if it's the one being modified
        if (selectedPlaylist?.id === playlistId) {
          setSelectedPlaylist(prev => ({
            ...prev,
            tracks: updatedTracks,
            updatedAt: new Date().toISOString()
          }));
        }

        setError(null);
        return true;
      } catch (err) {
        const errorMsg = err.message;
        setError(errorMsg);
        console.error('Error adding track to playlist:', err);
        return false;
      }
    },
    [userId, selectedPlaylist]
  );

  // Remove track from playlist
  const removeTrackFromPlaylist = useCallback(
    async (playlistId, trackId) => {
      if (!userId) {
        setError('User not authenticated');
        return false;
      }

      try {
        const playlistRef = dbRef(db, `users/${userId}/playlists/${playlistId}`);
        const snapshot = await get(playlistRef);
        const playlist = snapshot.val();

        if (!playlist) {
          setError('Playlist not found');
          return false;
        }

        const updatedTracks = (playlist.tracks || []).filter(t => t.id !== trackId);

        await update(playlistRef, {
          tracks: updatedTracks,
          updatedAt: new Date().toISOString()
        });

        // Update selectedPlaylist if it's the one being modified
        if (selectedPlaylist?.id === playlistId) {
          setSelectedPlaylist(prev => ({
            ...prev,
            tracks: updatedTracks,
            updatedAt: new Date().toISOString()
          }));
        }

        setError(null);
        return true;
      } catch (err) {
        const errorMsg = err.message;
        setError(errorMsg);
        console.error('Error removing track from playlist:', err);
        return false;
      }
    },
    [userId, selectedPlaylist]
  );

  // Update playlist details
  const updatePlaylist = useCallback(
    async (playlistId, updates) => {
      if (!userId) {
        setError('User not authenticated');
        return false;
      }

      try {
        const playlistRef = dbRef(db, `users/${userId}/playlists/${playlistId}`);
        const updatedData = {
          ...updates,
          updatedAt: new Date().toISOString()
        };
        
        await update(playlistRef, updatedData);

        // Update selectedPlaylist if it's the one being modified
        if (selectedPlaylist?.id === playlistId) {
          setSelectedPlaylist(prev => ({
            ...prev,
            ...updates,
            updatedAt: new Date().toISOString()
          }));
        }

        setError(null);
        return true;
      } catch (err) {
        const errorMsg = err.message;
        setError(errorMsg);
        console.error('Error updating playlist:', err);
        return false;
      }
    },
    [userId, selectedPlaylist]
  );

  // Delete playlist
  const deletePlaylist = useCallback(
    async (playlistId) => {
      if (!userId) {
        setError('User not authenticated');
        return false;
      }

      try {
        const playlistRef = dbRef(db, `users/${userId}/playlists/${playlistId}`);
        await remove(playlistRef);

        if (selectedPlaylist?.id === playlistId) {
          setSelectedPlaylist(null);
        }

        setError(null);
        return true;
      } catch (err) {
        const errorMsg = err.message;
        setError(errorMsg);
        console.error('Error deleting playlist:', err);
        return false;
      }
    },
    [userId, selectedPlaylist]
  );

  return {
    playlists,
    selectedPlaylist,
    setSelectedPlaylist,
    loading,
    error,
    createPlaylist,
    addTrackToPlaylist,
    removeTrackFromPlaylist,
    updatePlaylist,
    deletePlaylist
  };
};